import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertTriangle,
  ShieldAlert,
  Shield,
  ShieldX,
  TrendingUp,
  Plus,
  Trash2,
  GripVertical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { RisksBlock as RisksBlockType } from "@/canvas/types/blocks";
import type { Risk } from "@/canvas/types/content";
import { cn } from "@/lib/utils";
import { useCanvasStore } from "../../store";
import { generateItemId } from "../../utils/id";
import {
  AnalysisListCard,
  AnalysisListCardDragHandle,
  AnalysisListAddButton,
  AnalysisListEditableTitle,
  AnalysisListEditableHeaderTitle,
  AnalysisListHeader,
  AnalysisListMetric,
  AnalysisListStatusChip,
  AnalysisListStatusSelect,
  DocumentReferenceChips,
  replaceAnalysisListItems,
} from "./AnalysisList";

interface RisksBlockProps {
  readOnly?: boolean;
  block: RisksBlockType;
}

const severityConfig = {
  critical: {
    icon: ShieldX,
    iconClass: "text-danger",
    chipClass: "border-danger/18 bg-danger-muted/18 text-danger/85",
  },
  high: {
    icon: ShieldAlert,
    iconClass: "text-warning",
    chipClass: "border-warning/18 bg-warning-muted/14 text-warning/90",
  },
  medium: {
    icon: Shield,
    iconClass: "text-warning/70",
    chipClass: "border-warning/14 bg-warning-muted/8 text-warning/75",
  },
  low: {
    icon: TrendingUp,
    iconClass: "text-success",
    chipClass: "border-success/18 bg-success-muted/18 text-success/85",
  },
} as const;

const severityOrder: Risk["severity"][] = ["critical", "high", "medium", "low"];

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("sv-SE", {
    style: "currency",
    currency: "SEK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCompactCurrency(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M kr`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}k kr`;
  }
  return `${value} kr`;
}

interface RiskCardProps {
  risk: Risk;
  severityLabel: string;
  readOnly: boolean;
  onUpdateRisk: (updatedRisk: Risk) => void;
  onRemoveRisk: (riskId: string) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}

function RiskCard({
  risk,
  severityLabel,
  readOnly,
  onUpdateRisk,
  onRemoveRisk,
  dragHandleProps,
}: RiskCardProps) {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingMitigation, setIsEditingMitigation] = useState(false);
  const [localTitle, setLocalTitle] = useState(risk.title);
  const [localDescription, setLocalDescription] = useState(risk.description || "");
  const [localMitigation, setLocalMitigation] = useState(risk.mitigation || "");
  const config = severityConfig[risk.severity];
  const Icon = config.icon;

  useEffect(() => {
    if (isOpen && cardRef.current) {
      const timeoutId = setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isEditingTitle) setLocalTitle(risk.title);
  }, [risk.title, isEditingTitle]);

  useEffect(() => {
    if (!isEditingDescription) setLocalDescription(risk.description || "");
  }, [risk.description, isEditingDescription]);

  useEffect(() => {
    if (!isEditingMitigation) setLocalMitigation(risk.mitigation || "");
  }, [risk.mitigation, isEditingMitigation]);

  const hasDetails = Boolean(risk.description || risk.mitigation || risk.costEstimate || !readOnly);

  const handleStartEditingTitle = useCallback(() => {
    setLocalTitle(risk.title);
    setIsEditingTitle(true);
  }, [risk.title]);

  const handleStartEditingDescription = useCallback(() => {
    setLocalDescription(risk.description || "");
    setIsEditingDescription(true);
  }, [risk.description]);

  const handleStartEditingMitigation = useCallback(() => {
    setLocalMitigation(risk.mitigation || "");
    setIsEditingMitigation(true);
  }, [risk.mitigation]);

  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    const trimmed = localTitle.trim();
    if (!trimmed) {
      setLocalTitle(risk.title);
    } else if (trimmed !== risk.title) {
      onUpdateRisk({ ...risk, title: trimmed });
    }
  }, [localTitle, risk, onUpdateRisk]);

  const handleDescriptionBlur = useCallback(() => {
    setIsEditingDescription(false);
    const newDescription = localDescription.trim() || undefined;
    if (newDescription !== risk.description) {
      onUpdateRisk({ ...risk, description: newDescription });
    }
  }, [localDescription, risk, onUpdateRisk]);

  const handleSeverityChange = useCallback(
    (newSeverity: Risk["severity"]) => {
      onUpdateRisk({ ...risk, severity: newSeverity });
    },
    [risk, onUpdateRisk],
  );

  const handleMitigationBlur = useCallback(() => {
    setIsEditingMitigation(false);
    const newMitigation = localMitigation.trim() || undefined;
    if (newMitigation !== risk.mitigation) {
      onUpdateRisk({ ...risk, mitigation: newMitigation });
    }
  }, [localMitigation, risk, onUpdateRisk]);

  const trailing = (
    <>
      {risk.costEstimate !== null && risk.costEstimate !== undefined ? (
        <span className="text-sm font-mono text-foreground/60 tabular-nums">
          {formatCurrency(risk.costEstimate)}
        </span>
      ) : null}
      {readOnly ? (
        <AnalysisListStatusChip label={severityLabel} className={config.chipClass} />
      ) : (
        <AnalysisListStatusSelect
          value={risk.severity}
          displayLabel={severityLabel}
          onValueChange={(value) => handleSeverityChange(value as Risk["severity"])}
          options={[
            { value: "low", label: t("canvas.blocks.risks.severity.low") },
            { value: "medium", label: t("canvas.blocks.risks.severity.medium") },
            { value: "high", label: t("canvas.blocks.risks.severity.high") },
            { value: "critical", label: t("canvas.blocks.risks.severity.critical") },
          ]}
          className={config.chipClass}
        />
      )}
    </>
  );

  return (
    <AnalysisListCard
      open={isOpen}
      onOpenChange={setIsOpen}
      expandable={hasDetails}
      cardRef={cardRef}
      dragHandle={
        !readOnly && dragHandleProps ? (
          <AnalysisListCardDragHandle {...dragHandleProps}>
            <GripVertical className="h-4 w-4" />
          </AnalysisListCardDragHandle>
        ) : undefined
      }
      leading={
        <div className={cn("shrink-0", config.iconClass)}>
          <Icon className="h-4 w-4" />
        </div>
      }
      title={
        <div className="min-w-0">
          <AnalysisListEditableTitle
            value={risk.title}
            draftValue={localTitle}
            isEditing={isEditingTitle}
            isOpen={isOpen}
            readOnly={readOnly}
            onChange={setLocalTitle}
            onStartEditing={handleStartEditingTitle}
            onCommit={handleTitleBlur}
            onCancel={() => {
              setLocalTitle(risk.title);
              setIsEditingTitle(false);
            }}
            displayClassName="truncate"
          />
        </div>
      }
      trailing={trailing}
      body={
        <>
          {risk.description || !readOnly ? (
            <div className="flex flex-col gap-1">
              <p className="text-ui-eyebrow text-foreground/50">
                {t("canvas.blocks.risks.headers.circumstance")}
              </p>
              {isEditingDescription && !readOnly ? (
                <textarea
                  value={localDescription}
                  onChange={(event) => setLocalDescription(event.target.value)}
                  onBlur={handleDescriptionBlur}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      setLocalDescription(risk.description || "");
                      setIsEditingDescription(false);
                    }
                    event.stopPropagation();
                  }}
                  onClick={(event) => event.stopPropagation()}
                  className="text-ui-body-sm text-foreground/80 leading-relaxed w-full bg-transparent border-0 rounded px-1 -mx-1 py-0.5 resize-none focus-visible:ring-0 outline-none min-h-[1.5em] block"
                  style={{ fieldSizing: "content" } as React.CSSProperties}
                  autoFocus
                />
              ) : (
                <p
                  className={cn(
                    "text-ui-body-sm text-foreground/80 leading-relaxed whitespace-pre-wrap",
                    !risk.description && "text-foreground/50 italic",
                    !readOnly &&
                      "cursor-text hover:bg-muted/30 rounded px-1 -mx-1 py-0.5 transition-colors",
                  )}
                  onClick={() => {
                    if (!readOnly) handleStartEditingDescription();
                  }}
                >
                  {risk.description ||
                    (readOnly ? "" : t("canvas.blocks.risks.addDescription", "Add description..."))}
                </p>
              )}
            </div>
          ) : null}
          {risk.mitigation || !readOnly ? (
            <div className="flex flex-col gap-1">
              <p className="text-ui-eyebrow text-foreground/50">
                {t("canvas.blocks.risks.headers.mitigation")}
              </p>
              {isEditingMitigation && !readOnly ? (
                <textarea
                  value={localMitigation}
                  onChange={(event) => setLocalMitigation(event.target.value)}
                  onBlur={handleMitigationBlur}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      setLocalMitigation(risk.mitigation || "");
                      setIsEditingMitigation(false);
                    }
                    event.stopPropagation();
                  }}
                  onClick={(event) => event.stopPropagation()}
                  className="text-ui-body-sm text-foreground/80 leading-relaxed w-full bg-transparent border-0 rounded px-1 -mx-1 py-0.5 resize-none focus-visible:ring-0 outline-none min-h-[1.5em] block"
                  style={{ fieldSizing: "content" } as React.CSSProperties}
                  autoFocus
                />
              ) : (
                <p
                  className={cn(
                    "text-ui-body-sm text-foreground/80 leading-relaxed whitespace-pre-wrap",
                    !risk.mitigation && "text-foreground/50 italic",
                    !readOnly &&
                      "cursor-text hover:bg-muted/30 rounded px-1 -mx-1 py-0.5 transition-colors",
                  )}
                  onClick={() => {
                    if (!readOnly) handleStartEditingMitigation();
                  }}
                >
                  {risk.mitigation ||
                    (readOnly ? "" : t("canvas.blocks.risks.addMitigation", "Add mitigation..."))}
                </p>
              )}
            </div>
          ) : null}
        </>
      }
      footer={
        <>
          <div className="flex-1">
            <DocumentReferenceChips refs={risk.documentRefs} />
          </div>
          {!readOnly ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0 ml-4"
              onClick={(event) => {
                event.stopPropagation();
                onRemoveRisk(risk.id);
              }}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              {t("common.delete", "Delete")}
            </Button>
          ) : null}
        </>
      }
    />
  );
}

interface SortableRiskCardProps extends Omit<RiskCardProps, "dragHandleProps"> {
  id: string;
}

function SortableRiskCard({ id, ...props }: SortableRiskCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <RiskCard {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

export function RisksBlock({ block, readOnly = false }: RisksBlockProps) {
  const { t } = useTranslation();
  const defaultHeaderText = t("canvas.blocks.risks.headers.risk");
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [localHeader, setLocalHeader] = useState(block.title || defaultHeaderText);
  const updateBlockContent = useCanvasStore((state) => state.updateBlockContent);
  const updateBlock = useCanvasStore((state) => state.updateBlock);

  useEffect(() => {
    if (!isEditingHeader) setLocalHeader(block.title || defaultHeaderText);
  }, [block.title, defaultHeaderText, isEditingHeader]);

  const risks = useMemo<Risk[]>(() => {
    return (block.content.items as Risk[] | undefined) ?? [];
  }, [block.content.items]);

  const severityLabels: Record<Risk["severity"], string> = {
    low: t("canvas.blocks.risks.severity.low"),
    medium: t("canvas.blocks.risks.severity.medium"),
    high: t("canvas.blocks.risks.severity.high"),
    critical: t("canvas.blocks.risks.severity.critical"),
  };

  const headerText = block.title || defaultHeaderText;

  const handleStartEditingHeader = useCallback(() => {
    setLocalHeader(block.title || defaultHeaderText);
    setIsEditingHeader(true);
  }, [block.title, defaultHeaderText]);

  const handleHeaderBlur = useCallback(() => {
    setIsEditingHeader(false);
    const trimmedTitle = localHeader.trim();
    const newTitle = trimmedTitle && trimmedTitle !== defaultHeaderText ? trimmedTitle : undefined;
    if (newTitle !== block.title) {
      updateBlock(block.id, { title: newTitle });
    }
  }, [block.id, block.title, defaultHeaderText, localHeader, updateBlock]);

  const setRiskItems = useCallback(
    (items: Risk[]) => {
      updateBlockContent(
        block.id,
        replaceAnalysisListItems(block.content, items) as Parameters<typeof updateBlockContent>[1],
      );
    },
    [block.content, block.id, updateBlockContent],
  );

  const handleUpdateRisk = useCallback(
    (updatedRisk: Risk) => {
      setRiskItems(risks.map((risk) => (risk.id === updatedRisk.id ? updatedRisk : risk)));
    },
    [risks, setRiskItems],
  );

  const handleAddRisk = useCallback(() => {
    const newRisk: Risk = {
      id: generateItemId(),
      title: t("canvas.blocks.risks.newRiskTitle", "New risk"),
      severity: "medium",
      description: undefined,
    };
    setRiskItems([...risks, newRisk]);
  }, [risks, setRiskItems, t]);

  const handleRemoveRisk = useCallback(
    (riskId: string) => {
      setRiskItems(risks.filter((risk) => risk.id !== riskId));
    },
    [risks, setRiskItems],
  );

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = risks.findIndex((risk) => risk.id === active.id);
      const newIndex = risks.findIndex((risk) => risk.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const nextRisks = [...risks];
      const [movedRisk] = nextRisks.splice(oldIndex, 1);
      nextRisks.splice(newIndex, 0, movedRisk);
      setRiskItems(nextRisks);
    },
    [risks, setRiskItems],
  );

  const stats = useMemo(() => {
    const counts: Record<Risk["severity"], number> = {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
    };
    let totalCost = 0;

    risks.forEach((risk) => {
      counts[risk.severity]++;
      if (risk.costEstimate !== null && risk.costEstimate !== undefined) {
        totalCost += risk.costEstimate;
      }
    });

    return { counts, totalCost };
  }, [risks]);

  const sortedRisks = useMemo(() => {
    return [...risks].sort(
      (left, right) => severityOrder.indexOf(left.severity) - severityOrder.indexOf(right.severity),
    );
  }, [risks]);

  if (risks.length === 0) {
    return (
      <div className="rounded-xl border-2 border-dashed border-muted-foreground/20 p-8 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted/50 mb-3">
          <AlertTriangle className="h-6 w-6 text-muted-foreground/50" />
        </div>
        <p className="text-sm text-muted-foreground mb-3">{t("canvas.blocks.risks.empty")}</p>
        {!readOnly ? (
          <Button variant="outline" size="sm" onClick={handleAddRisk}>
            <Plus className="h-4 w-4 mr-1.5" />
            {t("canvas.blocks.risks.addRisk", "Add risk")}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <AnalysisListHeader
        title={
          <AnalysisListEditableHeaderTitle
            value={headerText}
            draftValue={localHeader}
            isEditing={isEditingHeader}
            readOnly={readOnly}
            onChange={setLocalHeader}
            onStartEditing={handleStartEditingHeader}
            onCommit={handleHeaderBlur}
            onCancel={() => {
              setLocalHeader(block.title || defaultHeaderText);
              setIsEditingHeader(false);
            }}
          />
        }
        metrics={severityOrder.map((severity) => {
          const count = stats.counts[severity];
          if (count === 0) return null;

          const config = severityConfig[severity];
          const Icon = config.icon;

          return (
            <AnalysisListMetric
              key={severity}
              icon={<Icon className={cn("h-4 w-4", config.iconClass)} />}
              value={count}
              tooltip={
                <p>
                  {count} {severityLabels[severity].toLowerCase()}{" "}
                  {count === 1
                    ? t("canvas.blocks.risks.riskSingular", "risk")
                    : t("canvas.blocks.risks.riskPlural", "risks")}
                </p>
              }
            />
          );
        })}
        trailing={
          stats.totalCost > 0 ? (
            <AnalysisListMetric
              icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
              value={formatCompactCurrency(stats.totalCost)}
              valueClassName="font-mono"
              className="text-sm"
              tooltip={
                <p>
                  {t("canvas.blocks.risks.totalEstimatedCost", "Total estimated cost")}:{" "}
                  {formatCurrency(stats.totalCost)}
                </p>
              }
            />
          ) : undefined
        }
      />

      {readOnly ? (
        <div className="flex flex-col gap-2">
          {sortedRisks.map((risk) => (
            <RiskCard
              key={risk.id}
              risk={risk}
              severityLabel={severityLabels[risk.severity]}
              readOnly={readOnly}
              onUpdateRisk={handleUpdateRisk}
              onRemoveRisk={handleRemoveRisk}
            />
          ))}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={risks.map((risk) => risk.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-2">
              {risks.map((risk) => (
                <SortableRiskCard
                  key={risk.id}
                  id={risk.id}
                  risk={risk}
                  severityLabel={severityLabels[risk.severity]}
                  readOnly={readOnly}
                  onUpdateRisk={handleUpdateRisk}
                  onRemoveRisk={handleRemoveRisk}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {!readOnly ? (
        <AnalysisListAddButton onClick={handleAddRisk}>
          <Plus className="h-4 w-4" />
          {t("canvas.blocks.risks.addRisk", "Add risk")}
        </AnalysisListAddButton>
      ) : null}
    </div>
  );
}
